import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./hero.css";
import videoHero from "../../assets/hero-final.mp4";
import persona1 from "../../assets/persona1.webp";
import persona2 from "../../assets/persona2.webp";
import CircularText from "./CircularText";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    // #region agent log
    // fetch('http://localhost:7242/ingest/7fbf4cdb-0330-43f8-bafc-0875e47882a3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'hero.jsx:102',message:'Hero useEffect start',data:{hasRoot:!!rootRef.current,isMobile},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    const root = rootRef.current;
    const pinHeight = root.querySelector(".pin-height");
    const container = root.querySelector(".container");
    const image = root.querySelector(".right");
    const cta = root.querySelector(".cta");
    const ctaMainGroup = root.querySelector(".cta-main-group");
    // const ctaSub = root.querySelector(".cta-sub");
    const ctaSubWrapper = root.querySelector(".cta-sub-wrapper");
    const heroModern = root.querySelector(".hero-modern");
    const metricValues = root.querySelectorAll(".hero-modern-metric-value");
    // #region agent log
    // fetch('http://localhost:7242/ingest/7fbf4cdb-0330-43f8-bafc-0875e47882a3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'hero.jsx:102',message:'DOM queries completed',data:{hasCtaSubWrapper:!!ctaSubWrapper,hasCtaMainGroup:!!ctaMainGroup,hasCta:!!cta},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion

    const renderMetricValues = () => {
      if (!metricValues.length) return;
      metricValues.forEach((el) => {
        const endValue = Number(el.dataset.countValue || 0);
        const prefix = el.dataset.countPrefix || "";
        const suffix = el.dataset.countSuffix || "";
        el.textContent = `${prefix}${Math.round(endValue)}${suffix}`;
      });
    };

    // Check if mobile (disable complex GSAP animations on mobile)
    if (isMobile) {
      // Simple mobile: just show everything, no complex animations
      if (heroModern) {
        heroModern.style.opacity = "1";
        heroModern.style.transform = "translateY(0)";
      }
      if (ctaMainGroup) {
        ctaMainGroup.style.opacity = "1";
        ctaMainGroup.style.transform = "translateY(0)";
      }
      if (ctaSubWrapper) {
        ctaSubWrapper.style.opacity = "1";
        ctaSubWrapper.style.transform = "translateY(0)";
      }
      if (cta) {
        cta.style.pointerEvents = "auto";
      }
      renderMetricValues();
      return;
    }

    // Desktop: Full GSAP animations
    ScrollTrigger.create({
      trigger: pinHeight,
      start: "top top",
      end: "bottom bottom",
      pin: container,
      scrub: true,
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: pinHeight,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        // markers: true,
      },
    });

    // === IMAGE SCROLL DOWN ===
    tl.to(
      image,
      {
        x: "-23vw",
        scale: 0.5,
        y: "-5vh",
        ease: "power3.inOut",
        duration: 1.2,
      }
    );

    if (heroModern) {
      tl.to(
        heroModern,
        {
          opacity: 0,
          y: 20,
          ease: "power3.inOut",
          duration: 1.2,
        },
        "<"
      );
    }

    if (metricValues.length) {
      metricValues.forEach((el) => {
        const endValue = Number(el.dataset.countValue || 0);
        const prefix = el.dataset.countPrefix || "";
        const suffix = el.dataset.countSuffix || "";
        if (el.dataset.counted === "true") return;
        el.dataset.counted = "true";

        const counter = { value: 0 };
        gsap.to(counter, {
          value: endValue,
          duration: 1.2,
          ease: "power2.out",
          onUpdate: () => {
            el.textContent = `${prefix}${Math.round(counter.value)}${suffix}`;
          },
        });
      });
    }

    // Store timeline reference for navbar to access
    window.heroTimeline = tl;
    
    // Calculate progress: video transition starts
    window.heroVideoTransitionProgress = 0.5;

    // === CTA APPEAR (AFTER IMAGE SETTLED) ===
    // #region agent log
    // fetch('http://localhost:7242/ingest/7fbf4cdb-0330-43f8-bafc-0875e47882a3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'hero.jsx:102',message:'Before GSAP cta animations',data:{hasCtaMainGroup:!!ctaMainGroup,hasCtaSubWrapper:!!ctaSubWrapper},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    if (ctaMainGroup) {
      tl.to(ctaMainGroup, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
      }, "-=0.4");
    }
    
    if (ctaSubWrapper) {
      // #region agent log
      // fetch('http://localhost:7242/ingest/7fbf4cdb-0330-43f8-bafc-0875e47882a3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'hero.jsx:102',message:'Animating ctaSubWrapper',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      tl.to(ctaSubWrapper, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        onComplete: () => {
          if (cta) cta.style.pointerEvents = "auto";
        },
      }, "-=0.6");
    } else {
      // #region agent log
      // fetch('http://localhost:7242/ingest/7fbf4cdb-0330-43f8-bafc-0875e47882a3',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'hero.jsx:102',message:'ctaSubWrapper is null - cannot animate',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
    }

    return () => {
      // Cleanup ScrollTriggers
      ScrollTrigger.getAll().forEach((st) => st.kill());
      // Clear timeline reference
      if (window.heroTimeline) {
        window.heroTimeline = null;
      }
      if (window.heroVideoTransitionProgress) {
        window.heroVideoTransitionProgress = null;
      }
    };
  }, [isMobile]);

  return (
    <section id="home" className="mwg_effect006" ref={rootRef}>
      <div className="pin-height">
        <div className="container">
          {/* <p className="name">
            Metagent
          </p> */}

          <div className="video-wrapper">
            <video
            className="right"
            src={videoHero}
            autoPlay
            loop
            muted
            playsInline
            />
            <div className="hero-modern">
              <div className="hero-modern-glass">
                <div className="hero-modern-inner">
                  <p className="hero-modern-eyebrow">
                    {tr("Metagent • AI Agent for Solopreneurs", "Metagent • AI Agent untuk Solopreneur")}
                  </p>
                  <h1 className="hero-modern-title">
                  {tr(
                    "You bring your workflow,",
                    "Kamu bawa workflow‑nya,"
                  )}{" "}
                  <br />{" "}
                  {tr("We turn it into AI Automation", "Kami ubah jadi AI Automation")}
                  </h1>
                  <p className="hero-modern-subtitle">
                    {tr(
                      "Whether you’re managing clients, content, or operations, we turn that workflow into",
                      "Apa pun yang kamu kelola—klien, konten, atau operasional—kami ubah workflow itu menjadi"
                    )}{" "}
                    <br />{" "}
                    <span className="hero-modern-highlight">
                      {tr("your own AI tool", "AI tool milikmu sendiri")}
                    </span>
                  </p>
                  <div className="hero-modern-actions">
                    
                    <a
                      href="#contact-us"
                      className="navbar-contact-button hero-contact-button"
                    >
                      {tr("Talk to Us", "Hubungi Kami")}
                    </a>
                    <button 
                      className="navbar-contact-button hero-contact-button hero-demo-button"
                      onClick={() => navigate('/portfolio')}
                    >
                      {tr("View AI Demo", "Lihat AI Demo")}
                    </button>
                  </div>
                  <div className="hero-persona-row">
                    <div className="hero-persona-avatars">
                      <img src={persona1} alt="Persona 1" />
                      <img src={persona2} alt="Persona 2" />
                    </div>
                    
                    <p className="hero-modern-subtitle hero-persona-text">
                      {tr("Trusted by 20+ solopreneurs", "Dipercaya 20+ solopreneur")}
                    </p>
                  </div>
                  <div className="hero-modern-metrics">
                    <div className="hero-modern-metric">
                      <span
                        className="hero-modern-metric-value"
                        data-count-value="4"
                        data-count-prefix="+"
                        data-count-suffix=" hrs"
                      >
                        +0 hrs
                      </span>
                      <span className="hero-modern-metric-label">
                        {tr("saved per day", "hemat per hari")}
                      </span>
                    </div>
                    <div className="hero-modern-metric">
                      <span
                        className="hero-modern-metric-value"
                        data-count-value="3"
                        data-count-suffix="x"
                      >
                        0x
                      </span>
                      <span className="hero-modern-metric-label">
                        {tr("faster execution", "eksekusi lebih cepat")}
                      </span>
                    </div>
                    <div className="hero-modern-metric">
                      <span
                        className="hero-modern-metric-value"
                        data-count-value="24"
                        data-count-suffix="/7"
                      >
                        0/7
                      </span>
                      <span className="hero-modern-metric-label">
                        {tr("agent uptime", "uptime agent")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="cta">
              <div className="cta-main-group">
                <h2 className="cta-main">
                  {tr(
                    "Solopreneurs today like content creators, online business owners, and professional service providers juggle everything from ideation, planning, execution, communication, to admin and operations",
                    "Solopreneur seperti content creator, online business owner, dan professional service provider harus menangani ideasi, perencanaan, eksekusi, komunikasi, hingga admin dan operasional"
                  )}
                </h2>
                <p className="cta-main-sub">
                  {tr(
                    "They do it all on their own, switching between too many tools and tasks everyday. AI can help simplify the mess, and we're here to make that happen",
                    "Mereka mengerjakan semuanya sendiri, berpindah‑pindah di banyak tools dan tugas setiap hari. AI bisa menyederhanakan kekacauan itu, dan kami siap mewujudkannya"
                  )}
                </p>
              </div>
            
              <div className="cta-sub-wrapper">
                
                
                <CircularText 
                  text="Metagent • Metagent • " 
                  spinDuration={20}
                  onHover="speedUp"
                  className="cta-circular-text"
                />
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
